// Домашний экран в gn-стиле (graphic novel): список кампаний + «продолжить игру»
// (featured) + быстрые плитки, экран создания, экран вступления (код / публичные /
// приглашения). Монтируется на уровне App ВНЕ легаси .cc-* иерархии (свой #gn-root),
// поэтому легаси-CSS сюда не дотягивается — кроме голых button/input правил, которые
// перекрыты «home guard» в gn.css. Дата-слой живёт в App.tsx; сюда всё приходит
// пропсами, локально храним только состояние форм.

import { useEffect, useState, type FormEvent } from "react";
import {
  api,
  type Campaign,
  type CampaignInvitation,
  type Me,
  type PublicCampaign,
} from "../api";

const GAME = "/assets/game-ui";
const COVER_PLACEHOLDER = `${GAME}/campaign_cover_placeholder.webp`;

type Mode = "home" | "create" | "join";
type JoinTab = "code" | "public" | "invites";

type GnHomeProps = {
  me: Me | null;
  campaigns: Campaign[];
  publicCampaigns: PublicCampaign[];
  invitations: CampaignInvitation[];
  busy: boolean;
  listRefreshing: boolean;
  syncProblem: boolean;
  error: string | null;
  onOpen: (id: string) => void;
  onCreate: (title: string, publicVisible: boolean) => Promise<boolean>;
  onJoinCode: (code: string) => Promise<boolean>;
  onJoinPublic: (id: string) => Promise<boolean>;
  onAccept: (id: string) => Promise<boolean>;
  onDecline: (id: string) => Promise<boolean>;
  onReload: () => void;
};

function statusLabel(status: Campaign["status"]): string {
  switch (status) {
    case "DRAFT":
      return "Подготовка мира";
    case "ACTIVE":
      return "Кампания идёт";
    case "PAUSED":
      return "Приостановлена";
    case "FINISHED":
      return "Хроника завершена";
  }
}
function statusShort(status: Campaign["status"]): string {
  switch (status) {
    case "DRAFT":
      return "Подготовка";
    case "ACTIVE":
      return "Идёт";
    case "PAUSED":
      return "Пауза";
    case "FINISHED":
      return "Финал";
  }
}
function sortCampaigns(list: Campaign[]): Campaign[] {
  return [...list].sort((a, b) => {
    if (a.hasUnread !== b.hasUnread) return a.hasUnread ? -1 : 1;
    if (a.status !== b.status) return a.status === "ACTIVE" ? -1 : b.status === "ACTIVE" ? 1 : 0;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
}

function IcInvite() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>
  );
}
function IcSearch() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
  );
}
function IcCode() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="8" cy="15" r="4" /><path d="m10.8 12.2 8.2-8.2M17 7l3-3M15 9l2 2" /></svg>
  );
}
function IcPlus() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
  );
}
function IcBack() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
  );
}

// Обложка кампании (пролог-иллюстрация). Тот же blob-паттерн, что в легаси.
function Cover({ campaign }: { campaign: Campaign }) {
  const [src, setSrc] = useState(COVER_PLACEHOLDER);
  const coverId = campaign.coverImage?.status === "READY" ? campaign.coverImage.id : undefined;
  useEffect(() => {
    let disposed = false;
    let objectUrl: string | null = null;
    setSrc(COVER_PLACEHOLDER);
    if (!coverId) return () => { disposed = true; };
    void api
      .cycleImageContent(campaign.id, coverId)
      .then((blob) => {
        if (disposed) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => { if (!disposed) setSrc(COVER_PLACEHOLDER); });
    return () => {
      disposed = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [campaign.id, coverId]);
  return <img className="gn-feat-img" src={src} alt="" />;
}

function CampaignCard({ campaign, onOpen }: { campaign: Campaign; onOpen: (id: string) => void }) {
  const role = campaign.memberRole === "OWNER" ? "Организатор" : "Участник";
  const vis = campaign.memberRole === "OWNER" ? (campaign.publicVisible ? " · Публичная" : " · Приватная") : "";
  return (
    <button type="button" className={`gn-ccard${campaign.hasUnread ? " unread" : ""}`} onClick={() => onOpen(campaign.id)}>
      <span className="gn-cst">{statusShort(campaign.status)}</span>
      <span className="gn-cmeta">{role} · {campaign.memberCount}/10{vis}</span>
      <b>{campaign.title}</b>
      <span className="gn-csub">
        {statusLabel(campaign.status)}
        {campaign.hasUnread ? ` · ${campaign.unreadCount} новых` : ""}
      </span>
    </button>
  );
}

export function GnHome(props: GnHomeProps) {
  const { me, campaigns, publicCampaigns, invitations, busy, listRefreshing, syncProblem, error, onOpen, onReload } = props;
  const [mode, setMode] = useState<Mode>("home");
  const [joinTab, setJoinTab] = useState<JoinTab>("code");
  const [title, setTitle] = useState("");
  const [pub, setPub] = useState(false);
  const [code, setCode] = useState("");

  const sorted = sortCampaigns(campaigns);
  const featured = sorted[0];

  async function submitCreate(e: FormEvent) {
    e.preventDefault();
    if (!title.trim() || busy) return;
    if (await props.onCreate(title.trim(), pub)) {
      setTitle("");
      setPub(false);
    }
  }
  async function submitCode(e: FormEvent) {
    e.preventDefault();
    if (!code.trim() || busy) return;
    if (await props.onJoinCode(code.trim())) setCode("");
  }
  function openJoin(tab: JoinTab) {
    setJoinTab(tab);
    setMode("join");
  }

  const errorBar = error ? (
    <div className="gn-panel gn-herror">
      <span>{error}</span>
      <button type="button" className="gn-secbtn" onClick={onReload}>Повторить</button>
    </div>
  ) : null;

  // ——— СОЗДАТЬ ———
  if (mode === "create") {
    return (
      <div id="gn-root" className="gn-root">
        <div className="gn-shell">
          <div className="gn-top">
            <button type="button" className="gn-back" onClick={() => setMode("home")} aria-label="Назад"><IcBack /></button>
            <div className="gn-tt"><b>Новая хроника</b><span>Создание кампании</span></div>
            <span className="gn-cog-spacer" aria-hidden="true" />
          </div>
          <div className="gn-screen">
            <form className="gn-body" onSubmit={submitCreate}>
              {errorBar}
              <div className="gn-panel">
                <label className="gn-lab" htmlFor="gn-new-title">Название кампании</label>
                <input id="gn-new-title" className="gn-oinput" maxLength={120} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Например, Совет Нерея" />
                <label className={`gn-switch${pub ? " on" : ""}`}>
                  <input type="checkbox" checked={pub} onChange={(e) => setPub(e.target.checked)} />
                  <span className="gn-sw" />
                  <span>
                    <b>Публичная кампания</b>
                    <small>Её смогут находить в поиске и запрашивать вступление. Можно выключить — тогда только по коду или приглашению.</small>
                  </span>
                </label>
              </div>
              <div className="gn-panel">
                <span className="gn-eyebrow gn-eyebrow-p">Что дальше</span>
                <p className="gn-omuted">После создания вы пригласите игроков, каждый предложит свой вариант мира, затем Совет проголосует — и мир сгенерируется с ролями для всех. Обычно 3–8 игроков.</p>
              </div>
              <button type="submit" className={`gn-cta${!title.trim() || busy ? " gn-dis" : ""}`} disabled={!title.trim() || busy}>
                Создать кампанию
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ——— ВСТУПИТЬ (код / публичные / приглашения) ———
  if (mode === "join") {
    return (
      <div id="gn-root" className="gn-root">
        <div className="gn-shell">
          <div className="gn-top">
            <button type="button" className="gn-back" onClick={() => setMode("home")} aria-label="Назад"><IcBack /></button>
            <div className="gn-tt"><b>Присоединиться</b><span>Код · Поиск · Приглашения</span></div>
            <span className="gn-cog-spacer" aria-hidden="true" />
          </div>
          <div className="gn-screen">
            <div className="gn-body">
              {errorBar}
              <div className="gn-seg gn-segwide" role="tablist">
                <button type="button" className={joinTab === "code" ? "on" : ""} onClick={() => setJoinTab("code")}>По коду</button>
                <button type="button" className={joinTab === "public" ? "on" : ""} onClick={() => setJoinTab("public")}>Публичные</button>
                <button type="button" className={joinTab === "invites" ? "on" : ""} onClick={() => setJoinTab("invites")}>Приглашения</button>
              </div>

              {joinTab === "code" && (
                <form className="gn-panel" onSubmit={submitCode}>
                  <label className="gn-lab" htmlFor="gn-join-code">Код приглашения</label>
                  <input id="gn-join-code" className="gn-oinput gn-codeinput" maxLength={16} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Например, 2VTARRWY" />
                  <div style={{ height: "11px" }} />
                  <button type="submit" className={`gn-cta${!code.trim() || busy ? " gn-dis" : ""}`} disabled={!code.trim() || busy}>
                    Войти в кампанию
                  </button>
                  <p className="gn-omuted" style={{ marginTop: "10px" }}>Код даёт организатор кампании. Регистр не важен.</p>
                </form>
              )}

              {joinTab === "public" && (
                <div className="gn-panel">
                  <span className="gn-eyebrow gn-eyebrow-p">Публичные игры</span>
                  {publicCampaigns.length === 0 ? (
                    <p className="gn-omuted">Пока нет публичных кампаний со свободными местами.</p>
                  ) : (
                    publicCampaigns.map((c) => (
                      <div className="gn-pubrow" key={c.id}>
                        <div className="gn-pubi">
                          <b>{c.title}</b>
                          <small>Организатор {c.ownerDisplayName} · {c.memberCount}/{c.maxPlayers}</small>
                        </div>
                        <button type="button" className="gn-joinbtn" disabled={busy || c.slotsLeft <= 0} onClick={() => void props.onJoinPublic(c.id)}>
                          {c.slotsLeft <= 0 ? "Мест нет" : "Войти"}
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}

              {joinTab === "invites" && (
                <div className="gn-panel">
                  <span className="gn-eyebrow gn-eyebrow-p">Приглашения</span>
                  {invitations.length === 0 ? (
                    <p className="gn-omuted">Активных приглашений нет.</p>
                  ) : (
                    invitations.map((inv) => (
                      <div className="gn-pubrow" key={inv.id}>
                        <div className="gn-pubi">
                          <b>{inv.campaignTitle}</b>
                          <small>От: {inv.invitedByDisplayName}</small>
                        </div>
                        <div className="gn-invactions">
                          <button type="button" className="gn-joinbtn" disabled={busy} onClick={() => void props.onAccept(inv.id)}>Принять</button>
                          <button type="button" className="gn-injbtn" disabled={busy} onClick={() => void props.onDecline(inv.id)}>Отклонить</button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ——— ДОМ (список) ———
  const onlineText = listRefreshing ? "Синхронизация" : syncProblem ? "Связь…" : "Онлайн";
  return (
    <div id="gn-root" className="gn-root">
      <div className="gn-shell">
        <div className="gn-hbrand">
          <h1>City Chronicle</h1>
          {me && <span>{me.displayName}</span>}
          <span className={`gn-online${syncProblem ? " warn" : ""}`}><i />{onlineText}</span>
        </div>
        <div className="gn-screen">
          <div className="gn-body">
            {errorBar}

            {featured ? (
              <button type="button" className={`gn-feat${featured.hasUnread ? " unread" : ""}`} onClick={() => onOpen(featured.id)}>
                <Cover campaign={featured} />
                <span className="gn-feat-sh" />
                <span className="gn-feat-in">
                  <span className="gn-eyebrow">Продолжить игру</span>
                  <b className="gn-feat-h">{featured.title}</b>
                  <span className="gn-feat-p">{statusLabel(featured.status)}</span>
                  <span className="gn-chips">
                    <span className="gn-chip">{featured.memberCount}/10 игроков</span>
                    {featured.memberRole === "OWNER" && <span className="gn-chip">{featured.publicVisible ? "Публичная" : "Приватная"}</span>}
                    {featured.hasUnread && <span className="gn-chip gold">{featured.unreadCount} новых</span>}
                  </span>
                  <span className="gn-cta gn-feat-cta">Открыть кампанию</span>
                </span>
              </button>
            ) : (
              <div className="gn-panel gn-hempty">
                <span className="gn-eyebrow">Продолжить игру</span>
                <b>Кампания ещё не выбрана</b>
                <p className="gn-omuted">Создайте новую хронику, войдите по коду или присоединитесь к публичной кампании.</p>
              </div>
            )}

            {sorted.length > 0 && (
              <div>
                <span className="gn-eyebrow">Мои кампании</span>
                <div className="gn-hlist">
                  {sorted.slice(0, 4).map((c) => (
                    <CampaignCard key={c.id} campaign={c} onOpen={onOpen} />
                  ))}
                </div>
              </div>
            )}

            <div className="gn-qgrid">
              <button type="button" className="gn-qtile" onClick={() => openJoin("invites")}>
                <span className="gn-qic"><IcInvite /></span>
                <span><b>Приглашения</b><small>{invitations.length ? `${invitations.length} новое` : "нет новых"}</small></span>
              </button>
              <button type="button" className="gn-qtile" onClick={() => openJoin("public")}>
                <span className="gn-qic"><IcSearch /></span>
                <span><b>Поиск</b><small>{publicCampaigns.length ? `${publicCampaigns.length} публичных` : "публичные игры"}</small></span>
              </button>
              <button type="button" className="gn-qtile" onClick={() => openJoin("code")}>
                <span className="gn-qic"><IcCode /></span>
                <span><b>Войти по коду</b><small>есть код</small></span>
              </button>
              <button type="button" className="gn-qtile" onClick={() => setMode("create")}>
                <span className="gn-qic"><IcPlus /></span>
                <span><b>Создать</b><small>новая хроника</small></span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
